import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import * as yup from 'yup'
import {
  Autocomplete,
  Box,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  GlobalStyles,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import Dialog from 'components/dialog/dialog'
import type {
  PartialCreatePopUpBannerModelProps,
  PartialPopUpBannerCarModelSegmentationModelProps,
  PartialPopUpBannerMemberTierSegmentationModelProps,
  PartialSegmentationDropdownValueModelProps
} from 'modules/partial'
import type { DetailStateProps, ErrorProps } from 'modules/types'
import Notification from 'components/notification'
import GLOBAL from 'modules/global'
import BackdropImage from 'components/backdropImage/backdropImage'
import {
  useGetCarModelDropdownMutation,
  useGetMemberTierDropdownMutation,
  useGetPopUpBannerDetailQuery,
  useUpdatePopUpBannerMutation
} from 'store/popUpBanner'
import FORMIK from 'modules/formik'
import GlobalStyle from 'modules/styles'
import Loading from 'components/loading/loading'
import DialogStyle from './style'

const Edit = ({ open, id, onClose }: DetailStateProps & { id: string | number }) => {
  if (!open) return <></>
  const { data, isFetching, isLoading } = useGetPopUpBannerDetailQuery(id)
  const loading = !data && (isLoading || isFetching)

  const [periodStart, setPeriodStart] = useState<Dayjs | null>(null)
  const [periodEnd, setPeriodEnd] = useState<Dayjs | null>(null)

  const [getCarModelDropdown, carModelDDL] = useGetCarModelDropdownMutation()
  const [getMemberTierDropdown, memberTierDDL] = useGetMemberTierDropdownMutation()
  const [updatePopUpBanner, update] = useUpdatePopUpBannerMutation()
  const [previewImage, setPreviewImage] = useState<string>('')
  const [previewBackdrop, setPreviewBackdrop] = useState<boolean>(false)

  const formatDate = (val: string): string =>
    !isEmpty(val) ? dayjs(val).format('YYYY/MM/DD HH:mm:ss') : ''

  const carModelError = 'You can up to 5 selection of Car Model'

  const scheme = yup.object<PartialCreatePopUpBannerModelProps>({
    id: yup.string().min(1).required('Id is required'),
    campaignName: yup.string().required('Campaign Name is required'),
    periodStart: yup.string().required('Period Start is required'),
    periodEnd: yup.string().required('Period End is required'),
    bannerType: yup.string().required('Banner Type is required'),
    segmentationType: yup.string().required('Segmentation Type is required'),
    imageBase64: yup.string().notRequired(),
    body: yup
      .string()
      .nullable()
      .notRequired()
      .max(150, 'Exceed maximum characters (150)')
      .transform((value) => (value === '' ? null : value)),
    title: yup
      .string()
      .nullable()
      .notRequired()
      .max(50, 'Exceed maximum characters (50)')
      .transform((value) => (value === '' ? null : value)),
    carModel: yup
      .array()
      .of(
        yup.object({
          id: yup.mixed().required(),
          name: yup.string().required()
        })
      )
      .max(5, carModelError)
      .nullable()
      .notRequired(),
    image: yup
      .mixed<File>()
      .nullable()
      .test('fileSize', 'Exceed maximum file sixe allowed (100kb)', (value) => {
        if (!value) return true
        return value.size <= 100 * 1024 // 100kb
      })
      .test('fileType', 'Format should be in jpg/png', (value) => {
        if (!value) return true
        return ['image/jpeg', 'image/png'].includes(value.type)
      })
  })

  const initialValues: PartialCreatePopUpBannerModelProps = {
    id: Number(id),
    campaignName: (data && data.campaignName) || '',
    periodStart: formatDate((data && data.periodStart) || ''),
    periodEnd: formatDate((data && data.periodEnd) || ''),
    bannerType: (data && data.bannerType) || '',
    title: (data && data.title) || '',
    body: (data && data.body) || '',
    ctaDescription: (data && data.ctaDescription) || '',
    segmentationType: (data && data.segmentationType) || '',
    segmentationValueJson: null,
    memberTier: null,
    carModel: null,
    ctaUrl: (data && data.ctaUrl) || '',
    imageBase64: '',
    status: (data && data.status) || '', // BE will determine the default status based on periodStart and periodEnd
    bannerUrl: (data && data.bannerUrl) || ''
  }

  const formik = FORMIK.useFormFormik<PartialCreatePopUpBannerModelProps>(
    scheme,
    initialValues,
    (values) => {
      const segmentationObjValue: PartialSegmentationDropdownValueModelProps[] | null = []

      const data = {
        ...values,
        segmentationObjValue
      }
      updatePopUpBanner(data)
    }
  )

  const error = formik.errors
  console.log('🚀 ~ Edit ~ error:', error)
  const touched = formik.touched

  const disabled = !formik.isValid

  const handleCarModel = (val: PartialSegmentationDropdownValueModelProps[]) => {
    const temp: PartialSegmentationDropdownValueModelProps[] = []
    val.map((item) => temp.push(Object.assign({}, item)))
    formik.setFieldValue('carModel', temp)
    if (temp.length > 5) {
      formik.setFieldError('carModel', carModelError)
    } else {
      const segmentationData: PartialPopUpBannerCarModelSegmentationModelProps = {
        carModel: temp
      }
      formik.setFieldValue('segmentationValueJson', segmentationData)
    }
  }

  const handleMemberTier = (val: PartialSegmentationDropdownValueModelProps[]) => {
    const temp: PartialSegmentationDropdownValueModelProps[] = []
    val.map((item) => temp.push(Object.assign({}, item)))
    formik.setFieldValue('memberTier', temp)
    const segmentationData: PartialPopUpBannerMemberTierSegmentationModelProps = {
      memberTier: temp
    }
    formik.setFieldValue('segmentationValueJson', segmentationData)
  }

  const handleChangePeriod = (date: Dayjs | null, type: string | null) => {
    const newDate = (!isEmpty(date) && date.format('YYYY/MM/DD HH:mm:ss')) || ''
    if (type === 'start') {
      setPeriodStart(date)
      formik.setFieldValue('periodStart', newDate)
    } else {
      setPeriodEnd(date)
      formik.setFieldValue('periodEnd', newDate)
    }
  }

  useEffect(() => {
    if (data) {
      getMemberTierDropdown()
      getCarModelDropdown()
      if (data.periodStart) {
        setPeriodStart(dayjs(data.periodStart))
        setPeriodEnd(dayjs(data.periodEnd))
      }

      if (data.bannerUrl) {
        setPreviewImage(data.bannerUrl)
      }

      let segmentationDataModel: PartialSegmentationDropdownValueModelProps[] | null = null
      if (
        data.segmentationType === 'carModel' ||
        (data.segmentationType === 'memberTier' && data.segmentationValue)
      ) {
        try {
          if (data.segmentationType === 'carModel') {
            const parsedObject = JSON.parse(
              data.segmentationValue
            ) as PartialPopUpBannerCarModelSegmentationModelProps

            if (parsedObject && Array.isArray(parsedObject.carModel)) {
              segmentationDataModel = parsedObject.carModel
            } else {
              console.warn(
                'Segmentation JSON parsed successfully but carModel is missing or not an array.'
              )
            }
          }
          if (data.segmentationType === 'memberTier') {
            const parsedObject = JSON.parse(
              data.segmentationValue
            ) as PartialPopUpBannerMemberTierSegmentationModelProps

            if (parsedObject && Array.isArray(parsedObject.memberTier)) {
              segmentationDataModel = parsedObject.memberTier
            } else {
              console.warn(
                'Segmentation JSON parsed successfully but carModel is missing or not an array.'
              )
            }
          }
        } catch (error) {
          console.error('Failed to parse segmentationValue JSON:', error)
        }
      }

      if (segmentationDataModel) {
        let targetField = ''

        if (data.segmentationType === 'carModel') {
          targetField = 'carModel'
        } else if (data.segmentationType === 'memberTier') {
          targetField = 'memberTier'
        }

        if (targetField === 'carModel') {
          formik.setFieldValue('segmentationValueJson', { carModel: segmentationDataModel })
        } else if (targetField === 'memberTier') {
          formik.setFieldValue('segmentationValueJson', { memberTier: segmentationDataModel })
        }
        formik.setFieldValue('carModel', targetField === 'carModel' ? segmentationDataModel : [])

        formik.setFieldValue(
          'memberTier',
          targetField === 'memberTier' ? segmentationDataModel : []
        )
      } else {
        formik.setFieldValue('carModel', [])
        formik.setFieldValue('memberTier', [])
        formik.setFieldValue('segmentationValueJson', null)
      }
    }
  }, [data])

  return (
    <>
      <Dialog
        open={open}
        title='Edit Pop Up Banner'
        onCancel={onClose}
        onSubmit={() => formik.handleSubmit()}
        loading={false}
        isDisabled={disabled}
        submitText='Save'
      >
        <Container {...DialogStyle.Container}>
          {loading && <Loading />}
          {!loading && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ display: 'flex', flex: 1, gap: 2, flexDirection: 'column' }}>
                <TextField
                  id='campaignName'
                  name='campaignName'
                  variant='outlined'
                  value={formik.values.campaignName}
                  label='Campaign Name'
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched &&
                    formik.touched.campaignName &&
                    Boolean(formik.errors.campaignName)
                  }
                  helperText={
                    formik.touched &&
                    formik.touched.campaignName &&
                    formik.errors &&
                    formik.errors.campaignName
                  }
                  fullWidth
                />
                <Box sx={{ width: '100%' }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                    <GlobalStyles
                      styles={{
                        '.MuiMultiSectionDigitalClock-root': {
                          scrollbarWidth: 'none !important',
                          msOverflowStyle: 'none !important'
                        },
                        '.MuiMultiSectionDigitalClock-root::-webkit-scrollbar': {
                          display: 'none !important'
                        }
                      }}
                    />
                    <DateTimePicker
                      value={periodStart}
                      minDate={dayjs()}
                      onChange={(value) => handleChangePeriod(value, 'start')}
                      onClose={() => formik.handleBlur({ target: { name: 'periodStart' } })}
                      slotProps={{
                        textField: {
                          name: 'periodStart',
                          onBlur: formik.handleBlur,
                          error: formik.touched.periodStart && Boolean(formik.errors.periodStart),
                          helperText: formik.touched.periodStart && formik.errors.periodStart,
                          required: true,
                          fullWidth: true,
                          sx: { width: '100%' }
                        },
                        popper: {
                          sx: {
                            '& .MuiMultiSectionDigitalClock-root': {
                              scrollbarWidth: 'none',
                              msOverflowStyle: 'none',
                              '&::-webkit-scrollbar': {
                                display: 'none'
                              }
                            }
                          }
                        }
                      }}
                      label='Period Start'
                      format='DD-MM-YYYY HH:mm'
                      sx={{ width: '100%' }}
                    />
                  </LocalizationProvider>
                </Box>
                <Box sx={{ width: '100%' }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
                    <GlobalStyles
                      styles={{
                        '.MuiMultiSectionDigitalClock-root': {
                          scrollbarWidth: 'none !important',
                          msOverflowStyle: 'none !important'
                        },
                        '.MuiMultiSectionDigitalClock-root::-webkit-scrollbar': {
                          display: 'none !important'
                        }
                      }}
                    />
                    <DateTimePicker
                      value={periodEnd}
                      minDate={dayjs()}
                      onChange={(value) => handleChangePeriod(value, 'end')}
                      onClose={() => formik.handleBlur({ target: { name: 'periodEnd' } })}
                      slotProps={{
                        textField: {
                          name: 'periodEnd',
                          onBlur: formik.handleBlur,
                          error: formik.touched.periodEnd && Boolean(formik.errors.periodEnd),
                          helperText: formik.touched.periodEnd && formik.errors.periodEnd,
                          required: true,
                          fullWidth: true,
                          sx: { width: '100%' }
                        },
                        popper: {
                          sx: {
                            '& .MuiMultiSectionDigitalClock-root': {
                              scrollbarWidth: 'none',
                              msOverflowStyle: 'none',
                              '&::-webkit-scrollbar': {
                                display: 'none'
                              }
                            }
                          }
                        }
                      }}
                      label='Period End'
                      format='DD-MM-YYYY HH:mm'
                      sx={{ width: '100%' }}
                    />
                  </LocalizationProvider>
                </Box>
                <FormControl fullWidth>
                  <InputLabel id='bannerType'>Banner Type</InputLabel>
                  <Select
                    labelId='bannerType'
                    id='bannerType'
                    name='bannerType'
                    variant='outlined'
                    label='Banner Type'
                    value={formik.values.bannerType || ''}
                    onChange={(event) => {
                      formik.setFieldValue('bannerType', event.target.value)
                      if (event.target.value === 'textOnly') {
                        formik.setFieldValue('imageBase64', '')
                        formik.setFieldValue('bannerUrl', '')
                        setPreviewImage('')
                      }
                    }}
                    error={
                      touched && Boolean(touched.bannerType) && error && Boolean(error.bannerType)
                    }
                  >
                    <MenuItem value='textOnly'>Text</MenuItem>
                    <MenuItem value='visualAndText'>Key Visual + Text</MenuItem>
                    <MenuItem value='visual'>Key Visual</MenuItem>
                  </Select>
                  {touched && touched.bannerType && error && (
                    <FormHelperText error id='targetOption'>
                      {error.bannerType}
                    </FormHelperText>
                  )}
                </FormControl>
                {(formik.values.bannerType === 'visualAndText' ||
                  formik.values.bannerType === 'visual') && (
                  <>
                    <Box>
                      {previewImage && (
                        <BackdropImage
                          open={previewBackdrop}
                          onOpen={() => setPreviewBackdrop(true)}
                          onClose={() => setPreviewBackdrop(false)}
                        >
                          <img
                            src={previewImage}
                            alt=''
                            style={{ height: '300px', width: '505px', objectFit: 'contain' }}
                          />
                        </BackdropImage>
                      )}
                    </Box>
                    <TextField
                      id='image'
                      name='image'
                      label='Upload Image'
                      type='file'
                      placeholder='Upload Image'
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ accept: 'image/png, image/gif, image/jpeg', shrink: true }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const files = e && e.target && e.target.files
                        if (files && files.length > 0 && !isEmpty(files)) {
                          formik.setFieldValue('image', files[0])
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          const reader: any = new FileReader()
                          reader.onload = () => {
                            formik.setFieldValue('imageBase64', reader.result as string)
                            setPreviewImage(reader.result)
                          }
                          reader.readAsDataURL(files[0])
                        } else {
                          formik.setFieldValue('image', null)
                          setPreviewImage('')
                        }
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched && formik.touched.image && Boolean(formik.errors.image)}
                      helperText={
                        formik.touched &&
                        formik.touched.image &&
                        formik.errors &&
                        formik.errors.image
                      }
                      fullWidth
                    />
                  </>
                )}
                {(formik.values.bannerType === 'textOnly' ||
                  formik.values.bannerType == 'visualAndText') && (
                  <>
                    <TextField
                      id='title'
                      name='title'
                      variant='outlined'
                      label='Title'
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched && formik.touched.title && Boolean(formik.errors.title)}
                      helperText={
                        formik.touched &&
                        formik.touched.title &&
                        formik.errors &&
                        formik.errors.title
                      }
                      fullWidth
                    />
                    <TextField
                      id='body'
                      name='body'
                      multiline
                      variant='outlined'
                      label='Body'
                      value={formik.values.body}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched && formik.touched.body && Boolean(formik.errors.body)}
                      helperText={
                        formik.touched && formik.touched.body && formik.errors && formik.errors.body
                      }
                      fullWidth
                    />
                  </>
                )}
                <TextField
                  id='ctaDescription'
                  name='ctaDescription'
                  multiline
                  variant='outlined'
                  label='CTA Description'
                  value={formik.values.ctaDescription}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched &&
                    formik.touched.ctaDescription &&
                    Boolean(formik.errors.ctaDescription)
                  }
                  helperText={
                    formik.touched &&
                    formik.touched.ctaDescription &&
                    formik.errors &&
                    formik.errors.ctaDescription
                  }
                  fullWidth
                />
                <TextField
                  id='ctaUrl'
                  name='ctaUrl'
                  value={formik.values.ctaUrl}
                  multiline
                  variant='outlined'
                  label='CTA Url'
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched && formik.touched.ctaUrl && Boolean(formik.errors.ctaUrl)}
                  helperText={
                    formik.touched && formik.touched.ctaUrl && formik.errors && formik.errors.ctaUrl
                  }
                  fullWidth
                />
                {/* Start - Segmentation type */}
                <FormControl fullWidth>
                  <InputLabel id='segmentationType'>Segmentation Type</InputLabel>
                  <Select
                    labelId='targetOption'
                    id='targetOption'
                    variant='outlined'
                    label='Target Options *'
                    value={formik.values.segmentationType || ''}
                    onChange={(event) => {
                      formik.setFieldValue('segmentationType', event.target.value)
                      formik.setFieldValue('carModel', [])
                      formik.setFieldValue('memberTier', [])
                    }}
                    error={
                      touched &&
                      Boolean(touched.segmentationType) &&
                      error &&
                      Boolean(error.segmentationType)
                    }
                  >
                    <MenuItem value='carModel'>Car Model</MenuItem>
                    <MenuItem value='memberTier'>Member Tier</MenuItem>
                  </Select>
                  {touched && touched.segmentationType && error && (
                    <FormHelperText error id='targetOption'>
                      {error.segmentationType}
                    </FormHelperText>
                  )}
                </FormControl>
                {formik.values.segmentationType == 'carModel' && (
                  <Autocomplete
                    multiple
                    options={
                      (carModelDDL &&
                        carModelDDL.data?.map((item) => ({
                          id: item.id,
                          name: item.name
                        }))) ||
                      []
                    }
                    getOptionLabel={(list) => list.name}
                    isOptionEqualToValue={(option, value) =>
                      option && value ? option.name == value.name : false
                    }
                    onOpen={() => getCarModelDropdown()}
                    onChange={(_, data) => handleCarModel(data)}
                    onBlur={formik.handleBlur}
                    ListboxProps={GlobalStyle.ListBox}
                    renderOption={(props, item) => (
                      <li {...props} key={item.id}>
                        {item.name}
                      </li>
                    )}
                    value={formik.values.carModel ?? []}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name='carModel'
                        label='Car Model'
                        required
                        error={
                          formik.touched &&
                          formik.touched?.carModel &&
                          Boolean(formik.errors.carModel)
                        }
                        helperText={
                          formik.touched &&
                          formik.touched?.carModel &&
                          formik.errors &&
                          typeof formik.errors?.carModel === 'string'
                            ? formik.errors.carModel
                            : ''
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {carModelDDL.isLoading && (
                                <CircularProgress color='inherit' size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                )}
                {formik.values.segmentationType === 'memberTier' && (
                  <Autocomplete
                    multiple
                    options={
                      (memberTierDDL &&
                        memberTierDDL.data?.map((item) => ({
                          id: item.id,
                          name: item.name
                        }))) ||
                      []
                    }
                    getOptionLabel={(list) => list.name}
                    isOptionEqualToValue={(option, value) =>
                      option && value ? option.name == value.name : false
                    }
                    onOpen={() => getMemberTierDropdown()}
                    onChange={(_, data) => handleMemberTier(data)}
                    onBlur={formik.handleBlur}
                    ListboxProps={GlobalStyle.ListBox}
                    renderOption={(props, item) => (
                      <li {...props} key={item.id}>
                        {item.name}
                      </li>
                    )}
                    value={formik.values.memberTier ?? []}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name='memberTier'
                        label='Member Tier Target'
                        required
                        error={
                          formik.touched &&
                          formik.touched?.memberTier &&
                          Boolean(formik.errors.memberTier)
                        }
                        helperText={
                          formik.touched &&
                          formik.touched?.memberTier &&
                          formik.errors &&
                          typeof formik.errors?.memberTier === 'string'
                            ? formik.errors.memberTier
                            : ''
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {memberTierDDL.isLoading && (
                                <CircularProgress color='inherit' size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                )}
              </Box>
            </Box>
          )}
        </Container>
      </Dialog>
      <Notification
        open={!update.isLoading && !update.isUninitialized}
        onClose={() => (update.isError ? update.reset() : location.reload())}
        isError={Boolean(update.error) && update.isError}
        message={GLOBAL.returnExceptionMessage(update.isError, update.error as ErrorProps)}
      />
    </>
  )
}

export default Edit
